import { IntegrationCheckOwner } from "@freightsimple/generated-hubble-openapi-client/models/IntegrationCheckOwner";
import { IntegrationCheckStatus } from "@freightsimple/generated-hubble-openapi-client/models/IntegrationCheckStatus";
import type { ShopifyHealthcheckResult } from "@freightsimple/generated-hubble-openapi-client/models/ShopifyHealthcheckResult";

// Shopify healthcheck (ENG-6394). Its own module rather than a block in
// integrationsInlineResponses.tsx: one realistic result is a hundred and fifty lines of it, and
// carrying it there put that file over the 400-line limit.
//
// A deliberately mixed result: the point of the dialog is that it distinguishes *which*
// prerequisite is wrong, so an all-green mock would document nothing.
// Typed against the generated model so drift becomes a typecheck failure rather than a runtime
// break in the dialog.
export const SHOPIFY_HEALTHCHECK_RESPONSE: ShopifyHealthcheckResult = {
  shopDomain: "lenmark.myshopify.com",
  linkState: "linked",
  ok: false,
  items: [
    {
      id: "link_state",
      name: "Store linked",
      status: IntegrationCheckStatus.Pass,
      detail: "Linked",
      owner: IntegrationCheckOwner.Merchant,
      inferred: false,
      dimensionsInShopify: false,
      offenders: [],
    },
    {
      id: "carrier_service_registered",
      name: "Carrier service registered",
      status: IntegrationCheckStatus.Pass,
      detail: "Registered",
      owner: IntegrationCheckOwner.FreightSimple,
      inferred: false,
      dimensionsInShopify: false,
      offenders: [],
    },
    {
      id: "webhook_gids_stored",
      name: "Webhook subscriptions recorded",
      status: IntegrationCheckStatus.Pass,
      detail: "All 4 topics recorded",
      owner: IntegrationCheckOwner.FreightSimple,
      inferred: false,
      dimensionsInShopify: false,
      offenders: [],
    },
    {
      id: "delivery_location_type",
      name: "Delivery location type",
      status: IntegrationCheckStatus.Pass,
      detail: "Retail",
      owner: IntegrationCheckOwner.FreightSimple,
      inferred: false,
      dimensionsInShopify: false,
      offenders: [],
    },
    {
      id: "origin_profile",
      name: "Warehouse (origin) profile",
      status: IntegrationCheckStatus.Warn,
      detail:
        "Not set — captured automatically on the first booking, but booking fails field-by-field until it is",
      owner: IntegrationCheckOwner.FreightSimple,
      inferred: false,
      dimensionsInShopify: false,
      offenders: [],
    },
    {
      id: "scopes_granted",
      name: "Access scopes granted",
      status: IntegrationCheckStatus.Fail,
      detail:
        "Store granted 5 scope(s); missing write_shipping. A scope added after this store linked isn't granted until the merchant reconnects — re-link required",
      owner: IntegrationCheckOwner.Merchant,
      inferred: false,
      dimensionsInShopify: false,
      offenders: [],
    },
    {
      id: "admin_api_reachable",
      name: "Shopify Admin API reachable",
      status: IntegrationCheckStatus.Pass,
      detail: "Admin API answered as 'Lenmark'",
      owner: IntegrationCheckOwner.Merchant,
      inferred: false,
      dimensionsInShopify: false,
      offenders: [],
    },
    {
      id: "plan_tier",
      name: "Shopify plan includes carrier-calculated shipping",
      // Pass, not the warning: this store has a carrier service registered two rows up, and both
      // checks read the same field — a warning here would be a state the backend can't produce.
      status: IntegrationCheckStatus.Pass,
      detail:
        "Grow — Shopify accepted our carrier service for this store, which it only does on a plan that carries third-party carrier-calculated shipping (Grow carries it with annual billing or the paid add-on)",
      owner: IntegrationCheckOwner.Merchant,
      // Reasoned from Shopify having accepted the registration, not read from the plan name — the
      // dialog renders that distinction, so the fixture has to carry a case of it.
      inferred: true,
      dimensionsInShopify: false,
      offenders: [],
    },
    {
      id: "callback_environment",
      name: "Rate callback points here",
      status: IntegrationCheckStatus.Fail,
      detail:
        "Shopify calls https://api.freightsimple.com but this environment is https://api.freightsimpledemo.com — this store's checkout is quoting against a different environment, or a dead dev tunnel",
      owner: IntegrationCheckOwner.FreightSimple,
      inferred: false,
      dimensionsInShopify: false,
      offenders: [],
    },
    {
      id: "orders_create_namespaces",
      name: "ORDERS_CREATE carries the fs metafield namespace",
      status: IntegrationCheckStatus.Pass,
      detail: "fs",
      owner: IntegrationCheckOwner.FreightSimple,
      inferred: false,
      dimensionsInShopify: false,
      offenders: [],
    },
    {
      id: "protected_customer_data",
      name: "Protected customer data access",
      status: IntegrationCheckStatus.Pass,
      detail:
        "Order webhooks exist, which Shopify only allows once the app is approved",
      owner: IntegrationCheckOwner.PartnerDashboard,
      inferred: true,
      dimensionsInShopify: false,
      offenders: [],
    },
    {
      id: "products_ready",
      name: "Products ready to quote",
      status: IntegrationCheckStatus.Fail,
      detail:
        "12 of 18 quotable — 4 with no Saved Commodity, 2 missing dimensions",
      owner: IntegrationCheckOwner.Merchant,
      inferred: false,
      // This merchant keeps dimensions in Shopify metafields, so the dialog's
      // "fix it in Shopify" branch renders for the dimensions failure.
      dimensionsInShopify: true,
      // Mixed on purpose: one resolvable product (deep link) and one that isn't, so both
      // renders of an offender row show up in the dialog.
      offenders: [
        { label: 'Cedar Planter Box 48"', numericId: "8214670180471" },
        { label: "Bulk Gravel (no SKU)" },
      ],
    },
    {
      id: "products_have_weight",
      name: "Products have a weight in Shopify",
      status: IntegrationCheckStatus.Pass,
      detail: "Every variant has a weight",
      owner: IntegrationCheckOwner.Merchant,
      inferred: false,
      dimensionsInShopify: false,
      offenders: [],
    },
  ],
};
